<template>
    <!--
        The agent's working queue, on every authenticated page.

        `z-30` deliberately: above page content, below `NotificationSlideOver`'s
        `z-40`, below HeadlessUI's dialog layer so a modal still covers it, and
        below the scheduler's own `z-50` bottom bar — the two can coexist, and when
        they do it is the bar bound to the selection the operator just made that
        has to stay actionable.

        Inset from the left by the sidebar width, exactly as the scheduler's bar
        is, so it does not run underneath the navigation. The inset is `lg`-only:
        the sidebar is `lg:w-16` / `lg:w-64` and occupies nothing below that
        breakpoint, so carrying the inset down to mobile left a dead gutter on the
        left and pushed the bar off the right edge.

        Unlike that bar this one publishes its height, because a fixed element
        overlays rather than pushes and an unreserved footer hides the last row of
        every table in the CRM.
    -->
    <div
        v-if="isRunning"
        ref="footerEl"
        class="fixed right-0 bottom-0 left-0 z-30 border-t bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.06)] lg:left-(--queue-sidebar-inset) print:hidden"
        :style="{ '--queue-sidebar-inset': sidebarInset }"
        data-test="sales-queue-footer"
    >
        <!--
            The queue itself, expanded upward out of the bar (design D21).

            A list of rows can afford what the one-line bar cannot, which is why
            **the phone number is written out here**. D17's icon-only rule is about
            the bar's width tracking the length of a customer's data on a bar that
            is on every page; a panel of rows has a phone column the same width for
            every row, so the rule does not reach it. An agent planning the next
            hour wants to see whose numbers they have.

            Bounded and internally scrolled, because D13 reserves the footer's real
            measured height out of `main` — a fifty-item list left unbounded would
            reserve the viewport and leave no page behind it. The ceiling is what
            keeps that reservation sane, and it is deliberately generous: a twelve-item
            queue fits without scrolling on a desktop, which is what makes the panel a
            plan rather than a peephole. Lower on mobile, where the collapsed card
            already takes 245px of a phone before the panel opens at all.
        -->
        <div
            v-if="isExpanded"
            class="max-h-[32vh] overflow-y-auto border-b lg:max-h-[55vh]"
            data-test="queue-panel"
        >
            <!--
                Choosing a row is the jump a progress segment already performs — the
                same `current_item_id` patch, letting the cursor watcher below drive
                the router. Two affordances for one operation is right here: the
                segments are a glance-and-jump on a bar with no labels, the rows are
                the same jump when the agent can see what they are jumping to.

                The `#item` slot renders **one** element and carries no comment of
                its own: `vuedraggable` counts slot nodes and refuses more than one,
                and a comment node is a node — it renders
                `Error: Item slot must have only one child` in place of the list.
            -->
            <draggable
                :list="panelItems"
                item-key="id"
                handle=".queue-drag-handle"
                :animation="200"
                tag="ul"
                class="divide-y"
                @start="onDragStart"
                @end="onDragEnd"
            >
                <template #item="{ element: row }">
                    <li>
                        <div
                            class="hover:bg-muval-gray-6 flex w-full cursor-pointer items-center gap-x-2 px-3 py-2 text-left transition"
                            :class="row.id === queue?.current_item_id ? 'bg-muval-gray-6' : null"
                            role="button"
                            tabindex="0"
                            :aria-label="`Go to item ${row.position}`"
                            :data-test="`queue-panel-row-${row.position}`"
                            @click="chooseRow(row, $event)"
                            @keydown.enter.prevent="stepTo(row)"
                            @keydown.space.prevent="stepTo(row)"
                        >
                            <SvgIcon
                                name="system-drag-indicator"
                                class="queue-drag-handle text-muval-gray-3 size-4 shrink-0 cursor-grab active:cursor-grabbing"
                                :data-test="`queue-panel-handle-${row.position}`"
                            />

                            <span class="text-muval-gray-3 w-5 shrink-0 text-xs tabular-nums">{{ row.position }}</span>

                            <!--
                                The same three colours the progress segments use, so
                                the panel and the bar never disagree about an item's
                                state — greyed when the action is gone, which is the
                                one thing a colour cannot say.
                            -->
                            <span
                                class="size-2 shrink-0 rounded-full"
                                :class="dotClass(row)"
                                :data-test="`queue-panel-state-${row.position}`"
                                :data-state="row.available ? segmentState(row) : 'unavailable'"
                            />

                            <SvgIcon
                                v-if="row.action"
                                :name="actionTypeIcon(row.action.action_type)"
                                class="text-muval-gray-2 size-4 shrink-0"
                            />

                            <!--
                                `sm:contents` dissolves this wrapper once there is
                                width for one line, so the name and the moment become
                                items of the row directly — the same trick the bar's
                                own `lg:contents` uses, and for the same reason: one
                                instance of each, moved by the layout rather than
                                duplicated per breakpoint. Below `sm` the moment
                                stacks under the name instead of being hidden; every
                                field the panel promises is present at every width.
                            -->
                            <div class="min-w-0 flex-1 sm:contents">
                                <span class="text-muval-gray-1 block truncate text-sm sm:min-w-0 sm:flex-1">
                                    {{ row.action?.order?.customer_name ?? '--' }}
                                    <span
                                        v-if="row.action?.order?.ref"
                                        class="text-muval-gray-2"
                                        >#{{ row.action.order.ref }}</span
                                    >
                                </span>
                                <span
                                    class="text-muval-gray-2 block truncate text-xs sm:shrink-0 sm:whitespace-nowrap"
                                    :data-test="`queue-panel-moment-${row.position}`"
                                >
                                    {{ momentLabel(row.action) }}
                                </span>
                            </div>

                            <!--
                                `phone_raw` for the href, matching the collapsed bar
                                and `OrderCustomerCard` — the queue must dial
                                identically wherever it offers to. `@click.stop` so
                                dialling is not also a jump.
                            -->
                            <a
                                v-if="row.action?.order?.customer_phone_raw"
                                :href="`tel:${row.action.order.customer_phone_raw}`"
                                class="text-brand shrink-0 text-sm whitespace-nowrap tabular-nums hover:underline"
                                :data-test="`queue-panel-phone-${row.position}`"
                                @click.stop
                            >
                                {{ row.action.order.customer_phone_raw }}
                            </a>
                        </div>
                    </li>
                </template>
            </draggable>
        </div>

        <!--
            One segment per item, coloured by its derived state and clickable to
            jump. The denominator never moves, so the bar is a map of the committed
            work rather than a percentage that shrinks as it is worked.
        -->
        <div
            class="bg-muval-gray-6 flex h-1.5 w-full gap-px"
            data-test="queue-progress"
        >
            <button
                v-for="item in items"
                :key="item.id"
                type="button"
                class="h-full flex-1 transition-colors disabled:cursor-wait"
                :class="segmentClass(item)"
                :disabled="isWriting"
                :aria-label="`Go to item ${item.position}`"
                :data-test="`queue-segment-${item.position}`"
                :data-state="segmentState(item)"
                @click="jumpTo(item)"
            />
        </div>

        <!--
            Three zones (design D20) — *what and when*, *who*, and *what to do
            about it* — one row on desktop and a stacked card on mobile, with the
            rules falling where the register changes and turning from horizontal
            to vertical with the axis.
        -->
        <div class="flex flex-col lg:flex-row lg:items-center">
            <!--
                On mobile this is the card's first row: the action on the left, the
                queue's own controls on the right. `lg:contents` dissolves it on
                desktop so both become items of the outer row directly — which is
                what lets the controls take `order-last` and sit at the far right
                while the action stays first. One instance of each, moved by the
                layout rather than duplicated per breakpoint.
            -->
            <div class="flex items-start justify-between gap-x-3 px-4 py-2.5 lg:contents">
                <!--
                    ZONE 1 — what and when. The description sits under the type and
                    time, in italics, as the annotation on the appointment it
                    belongs to; it renders at every width, because the one field
                    that says *why this call* was previously invisible at exactly
                    the width an agent works from their phone.
                -->
                <div class="min-w-0 lg:shrink-0 lg:border-r lg:px-4 lg:py-2.5">
                    <template v-if="currentAction">
                        <div class="text-muval-gray-1 flex items-center gap-x-2">
                            <SvgIcon
                                :name="actionTypeIcon(currentAction.action_type)"
                                class="size-4 shrink-0"
                            />
                            <span class="text-sm font-medium whitespace-nowrap">{{
                                actionTypeLabel(currentAction.action_type)
                            }}</span>
                            <span
                                class="text-sm whitespace-nowrap"
                                :class="currentAction.overdue ? 'text-muval-red-1 font-medium' : 'text-muval-gray-1'"
                                data-test="queue-schedule"
                            >
                                {{ scheduleLabel }}
                            </span>
                        </div>
                        <MuvalTippy v-if="currentAction.note">
                            <p
                                class="text-muval-gray-2 mt-0.5 max-w-64 truncate text-sm italic"
                                data-test="queue-note"
                            >
                                {{ currentAction.note }}
                            </p>
                            <template #content>
                                <span class="block max-w-80 text-sm whitespace-pre-wrap">{{ currentAction.note }}</span>
                            </template>
                        </MuvalTippy>
                    </template>
                </div>

                <!--
                    The queue's own controls, grouped away from the item's exits so
                    that acting on the queue is never adjacent to acting on the
                    item — the position between its two steppers, and beneath them
                    the two stops.

                    Stepping writes no outcome: it moves the cursor and nothing
                    else. The stops are bordered circles rather than bare glyphs,
                    which reads as a control and widens the two targets D16's
                    confirmation exists to protect.
                -->
                <div class="flex shrink-0 flex-col items-center gap-y-1.5 lg:order-last lg:border-l lg:px-4 lg:py-2.5">
                    <div class="flex items-center gap-x-1">
                        <button
                            type="button"
                            class="text-muval-gray-2 hover:bg-muval-gray-6 hover:text-muval-gray-1 rounded-full p-1 transition disabled:pointer-events-none disabled:opacity-30"
                            :disabled="!previousItem || isWriting"
                            aria-label="Previous item"
                            data-test="queue-step-back"
                            @click="stepTo(previousItem)"
                        >
                            <SvgIcon
                                name="system-chevron-left"
                                class="size-5"
                            />
                        </button>
                        <span
                            class="text-muval-gray-1 text-sm font-medium whitespace-nowrap tabular-nums"
                            data-test="queue-position"
                        >
                            {{ progress.position || '--' }}/{{ progress.total }}
                        </span>
                        <button
                            type="button"
                            class="text-muval-gray-2 hover:bg-muval-gray-6 hover:text-muval-gray-1 rounded-full p-1 transition disabled:pointer-events-none disabled:opacity-30"
                            :disabled="!nextItem || isWriting"
                            aria-label="Next item"
                            data-test="queue-step-forward"
                            @click="stepTo(nextItem)"
                        >
                            <SvgIcon
                                name="system-chevron-right"
                                class="size-5"
                            />
                        </button>
                    </div>

                    <!--
                        Two ways to stop, and the difference between them is
                        whether the queue survives.

                        Pausing sets it aside: the bar goes away, the cursor and
                        every item stay exactly as they are, and the Activity
                        slide-out is where the agent picks it back up. Closing ends
                        it. Both are offered because an agent who needs the bar gone
                        right now should not have to destroy a half-worked day to
                        get it.
                    -->
                    <div class="flex items-center gap-x-1.5">
                        <!--
                            A queue control, not an item exit, so it belongs on this
                            side of the rule with pause and close (design D21). It
                            opens the queue itself upward out of the bar — the one
                            place the whole line is visible and re-orderable.

                            Client state, and deliberately unlike pausing: D15 made
                            `paused_at` a column because a queue set aside on a
                            laptop should still be set aside on a phone. Expansion
                            earns none of that — it is a glance at a list, one click
                            to redo, and a panel that reopened itself on a machine
                            the agent had not opened it on would be a surprise.
                        -->
                        <MuvalTippy>
                            <button
                                type="button"
                                class="text-muval-gray-1 hover:border-brand hover:text-brand flex size-7 items-center justify-center rounded-full border transition"
                                :aria-label="isExpanded ? 'Hide the queue' : 'Show the queue'"
                                :aria-expanded="isExpanded"
                                data-test="queue-expand"
                                @click="isExpanded = !isExpanded"
                            >
                                <SvgIcon
                                    :name="isExpanded ? 'system-chevron-down' : 'system-chevron-up'"
                                    class="size-4"
                                />
                            </button>
                            <template #content>
                                <span class="text-sm">{{
                                    isExpanded ? 'Hide the queue' : 'Show the whole queue'
                                }}</span>
                            </template>
                        </MuvalTippy>
                        <MuvalTippy>
                            <button
                                type="button"
                                class="text-muval-gray-1 hover:border-brand hover:text-brand flex size-7 items-center justify-center rounded-full border transition disabled:pointer-events-none disabled:opacity-40"
                                :disabled="isWriting"
                                aria-label="Pause queue"
                                data-test="queue-pause"
                                @click="askToStop('pause')"
                            >
                                <SvgIcon
                                    name="system-pause"
                                    class="size-4"
                                />
                            </button>
                            <template #content>
                                <span class="text-sm">Pause — find it again under Activity</span>
                            </template>
                        </MuvalTippy>
                        <MuvalTippy>
                            <button
                                type="button"
                                class="text-muval-gray-1 hover:border-brand hover:text-brand flex size-7 items-center justify-center rounded-full border transition disabled:pointer-events-none disabled:opacity-40"
                                :disabled="isWriting"
                                aria-label="Close queue"
                                data-test="queue-close"
                                @click="askToStop('close')"
                            >
                                <SvgIcon
                                    name="system-close"
                                    class="size-4"
                                />
                            </button>
                            <template #content>
                                <span class="text-sm">End this queue</span>
                            </template>
                        </MuvalTippy>
                    </div>
                </div>
            </div>

            <!--
                ZONE 2 — who. The customer, the order they belong to, and the two
                ways of reaching them.

                Both contact affordances are icons, and neither renders its value
                (design D17). This bar is on every page and re-renders on every
                advance, so a value rendered inline makes its width track the
                length of a customer's data — one rule for both is what keeps the
                layout still from item to item. Neither value is one an agent
                reads: the number is something you press, and the tooltip is there
                for the case where you want to read it out. Both keep their value
                in the `aria-label`, so nothing is lost that a screen reader could
                see.

                Phone before email (design D20): the ordering that put the email
                first was chosen while the phone still rendered its digits, and
                D17 removed that asymmetry. `CALL` is the commonest type here.

                `phone_raw` for the `tel:` href, matching OrderCustomerCard — the
                queue and the order screen have to dial identically. Both
                affordances are omitted rather than disabled when the customer has
                no number or address: a dead `tel:` is worse than none.

                The email is deliberately not a `mailto:` (design D18). An external
                client records nothing on the order, so the email drives the router
                to the item's order and opens the CRM's own composer there — which
                also leaves `CommAction`'s three gates deciding whether this order
                may be emailed at all, rather than a second set of rules living
                here.
            -->
            <div
                v-if="currentAction"
                class="min-w-0 border-t px-4 py-2.5 lg:flex-1 lg:border-t-0"
                data-test="queue-current"
            >
                <div class="flex min-w-0 items-center gap-x-2">
                    <router-link
                        v-if="currentAction.order"
                        :to="{ name: 'OrderView', params: { id: currentAction.order.id } }"
                        class="text-muval-gray-1 truncate text-sm font-medium hover:underline"
                        data-test="queue-order-link"
                    >
                        {{ currentAction.order.customer_name ?? '--' }}
                        <span class="text-muval-gray-1">#{{ currentAction.order.ref }}</span>
                    </router-link>

                    <div class="flex shrink-0 items-center gap-x-1">
                        <MuvalTippy v-if="currentAction.order?.customer_phone_raw">
                            <a
                                :href="`tel:${currentAction.order.customer_phone_raw}`"
                                class="text-muval-gray-1 hover:border-brand hover:text-brand flex size-7 items-center justify-center rounded-full border transition"
                                :aria-label="`Call ${currentAction.order.customer_phone_raw}`"
                                data-test="queue-call"
                            >
                                <SvgIcon
                                    name="system-phone"
                                    class="size-3.5"
                                />
                            </a>
                            <template #content>
                                <span class="text-sm">{{ currentAction.order.customer_phone_raw }}</span>
                            </template>
                        </MuvalTippy>
                        <MuvalTippy v-if="currentAction.order?.customer_email">
                            <button
                                type="button"
                                class="text-muval-gray-1 hover:border-brand hover:text-brand flex size-7 items-center justify-center rounded-full border transition disabled:cursor-wait"
                                :aria-label="`Email ${currentAction.order.customer_email}`"
                                :disabled="isComposingEmail"
                                data-test="queue-email"
                                @click="composeEmail"
                            >
                                <SvgIcon
                                    :name="isComposingEmail ? 'spinner' : 'system-email-filled'"
                                    class="size-3.5"
                                    :class="isComposingEmail ? 'animate-spin' : null"
                                />
                            </button>
                            <template #content>
                                <span class="text-sm">{{ currentAction.order.customer_email }}</span>
                            </template>
                        </MuvalTippy>
                    </div>
                </div>

                <!--
                    The route carries a `system-chevron-right` glyph between its two
                    ends rather than a `→` in the string. The character rendered in
                    whatever the text font happened to have for it, at the text's own
                    weight and colour, so it read as punctuation rather than as the
                    direction it is; splitting the label is what lets the glyph be
                    coloured and sized on its own.

                    A chevron rather than the `system-forward` arrow `MyOrderAction`
                    uses between the same two localities, per the designer — the arrow
                    is the Complete button's glyph in this bar, and one arrow that
                    means "go on to the next item" reads badly two elements away from
                    another that only separates two place names.
                -->
                <div
                    v-if="routeLeg"
                    class="text-muval-gray-2 mt-0.5 flex min-w-0 items-center gap-x-1 text-xs"
                    data-test="queue-route"
                >
                    <span class="truncate">{{ routeLeg.pickup }}</span>
                    <SvgIcon
                        name="system-chevron-right"
                        class="text-muval-gray-3 size-3.5 shrink-0"
                    />
                    <span class="truncate">{{ routeLeg.delivery }}</span>
                </div>
            </div>

            <!--
                The item is in the queue — membership is frozen — but its action
                has been deleted, so there is nothing left to work. Reached only by
                jumping to it: advancing walks past it.
            -->
            <div
                v-else
                class="text-muval-gray-2 border-t px-4 py-2.5 text-sm lg:flex-1 lg:border-t-0"
                data-test="queue-unavailable"
            >
                This action is no longer available.
            </div>

            <!--
                The four exits, reading in ascending commitment — move it, pass it,
                kill it, finish it — with only the last of them filled. Complete
                sat first for a while, which put the heaviest control where the eye
                lands and gave all four equal weight; one primary at the end of the
                row says which one is the exit and which three are the escapes
                (design D20).

                On mobile the three escapes share a row and `Complete` takes a
                full-width one beneath them.
            -->
            <div
                v-if="currentItem?.available"
                class="flex flex-col gap-2 border-t px-4 py-2.5 lg:ml-auto lg:shrink-0 lg:flex-row lg:items-center lg:border-t-0"
            >
                <div class="flex items-center gap-2">
                    <!--
                        Rescheduling is the load-bearing exit: a follow-up queue
                        whose only way out is *complete* is abandoned at the first
                        unanswered call.

                        The presets are the moments an agent actually picks, and
                        they move the *date* while keeping the action's own time of
                        day — an action due at 10am moved to "Tomorrow" is due at
                        10am tomorrow, not at some hour the button chose. The
                        picker below is the same control the create form uses, so
                        an agent settling "when" in the footer settles it exactly
                        as they would on the form.

                        A hand-rolled popover rather than `MPDropdown`, for one
                        reason: reka-ui's menu owns keydown for its typeahead, so
                        the picker inside its content loses the keystrokes it is
                        there to receive. The panel opens upward because the footer
                        is pinned to the bottom of the viewport.
                    -->
                    <div
                        ref="rescheduleEl"
                        class="relative flex-1 lg:flex-none"
                    >
                        <button
                            type="button"
                            class="h-input-small border-muval-gray-7 text-muval-gray-1 hover:border-brand hover:text-brand flex w-full items-center justify-center rounded-full border px-3 text-sm font-normal transition disabled:pointer-events-none disabled:opacity-40"
                            :disabled="isWriting"
                            data-test="queue-reschedule-trigger"
                            @click="toggleReschedule()"
                        >
                            Reschedule
                        </button>
                        <div
                            v-if="showReschedule"
                            class="shadow-muval-3 absolute bottom-full left-0 z-10 mb-2 w-64 rounded-lg border bg-white p-2 lg:right-0 lg:left-auto"
                            data-test="queue-reschedule-panel"
                        >
                            <button
                                v-for="preset in reschedulePresets"
                                :key="preset.key"
                                type="button"
                                class="text-muval-gray-1 hover:bg-muval-gray-6 flex w-full cursor-pointer items-center justify-between rounded-sm px-2 py-2 text-left text-sm transition"
                                :data-test="`queue-reschedule-${preset.key}`"
                                @click="reschedule(preset.value, rescheduleAllDay)"
                            >
                                <span>{{ preset.label }}</span>
                                <span class="text-muval-gray-3 text-xs">{{ preset.hint }}</span>
                            </button>

                            <div class="my-2 border-t" />

                            <!--
                                `w-full!` because the control sizes itself to one
                                of four fixed widths, none of which is the panel's.
                                Teleported so its menu escapes the footer, which is
                                a fixed element the picker would otherwise open
                                inside and be clipped by.
                            -->
                            <MPDatetime
                                v-model="rescheduleAt"
                                :has-time="!rescheduleAllDay"
                                :min-date="new Date()"
                                teleport
                                small
                                class="w-full!"
                                data-test="queue-reschedule-picker"
                            />

                            <div class="mt-2 flex items-center justify-between">
                                <label class="text-muval-gray-1 flex cursor-pointer items-center gap-x-2 text-sm">
                                    <MuvalSwitch
                                        v-model="rescheduleAllDay"
                                        data-test="queue-reschedule-all-day"
                                    />
                                    All day
                                </label>
                                <button
                                    type="button"
                                    class="bg-brand shrink-0 rounded-full px-3 py-1 text-sm text-white disabled:opacity-40"
                                    :disabled="!rescheduleAt"
                                    data-test="queue-reschedule-apply"
                                    @click="reschedule(rescheduleAt, rescheduleAllDay)"
                                >
                                    Reschedule
                                </button>
                            </div>
                        </div>
                    </div>

                    <MuvalButton
                        small
                        quinary
                        dataCy="queue-skip"
                        class="flex-1 lg:flex-none"
                        :loading="pendingOutcome === 'SKIPPED'"
                        :disabled="isWriting"
                        @click.prevent="exit('SKIPPED')"
                    >
                        Skip
                    </MuvalButton>
                    <MuvalButton
                        small
                        quinary
                        dataCy="queue-cancel"
                        class="flex-1 lg:flex-none"
                        :loading="pendingOutcome === 'CANCELLED'"
                        :disabled="isWriting"
                        @click.prevent="exit('CANCELLED')"
                    >
                        Cancel
                    </MuvalButton>
                </div>

                <!--
                    `system-forward` is the arrow `Skip` used to carry; the glyph
                    transfers rather than arrives, because there is no
                    `system-arrow-right` in the icon set. `iconAnimate` nudges it
                    on hover, which suits the one control that moves the queue on.

                    `w-full! lg:w-auto!` because `MuvalButton`'s own root is
                    `w-full md:w-fit` — the primary has to stay full-width all the
                    way up to this component's own `lg` breakpoint, not `md`, or it
                    shrinks to content in the band between the two.
                -->
                <MuvalButton
                    small
                    icon="system-forward"
                    iconRight
                    iconAnimate
                    dataCy="queue-complete"
                    class="w-full! lg:w-auto!"
                    :loading="pendingOutcome === 'COMPLETED'"
                    :disabled="isWriting"
                    @click.prevent="exit('COMPLETED')"
                >
                    Complete
                </MuvalButton>
            </div>
        </div>
    </div>

    <!--
        Both ways of stopping ask first.

        They sit one click apart and do opposite things to a half-worked day: one
        sets the queue down where it can be picked up, the other ends it for good.
        A misfire either way costs the agent their place in a list they had to
        build, so the modal's job is less "are you sure" than telling them which
        of the two they are about to do.

        One modal for both, because they are mutually exclusive and the only thing
        that differs is what it says.
    -->
    <MPModal
        v-model="showStopConfirm"
        :title="stopCopy.title"
        size="sm"
    >
        <div
            class="flex flex-col"
            data-test="queue-stop-confirm"
        >
            <p
                class="text-muval-gray-1 mb-2 text-sm"
                data-test="queue-stop-confirm-body"
            >
                {{ stopCopy.body }}
            </p>
            <MuvalBanner
                :type="stopIntent === 'close' ? 'danger' : 'alert'"
                class="mb-2"
                ><p
                    class="text-sm"
                    data-test="queue-stop-confirm-outstanding"
                >
                    {{ stopCopy.consequence }}
                </p></MuvalBanner
            >
            <div class="flex flex-col justify-end gap-2 md:flex-row">
                <MuvalButton
                    cancelBtn
                    class="order-2 md:order-1"
                    type="button"
                    dataCy="queue-stop-cancel"
                    @click.prevent="showStopConfirm = false"
                >
                    Keep working
                </MuvalButton>
                <MuvalButton
                    class="order-1 md:order-2"
                    type="button"
                    dataCy="queue-stop-accept"
                    :loading="isWriting"
                    @click.prevent="confirmStop()"
                >
                    {{ stopCopy.confirm }}
                </MuvalButton>
            </div>
        </div>
    </MPModal>
</template>

<script setup>
import dayjs from 'dayjs';
import draggable from 'vuedraggable';
import { toast } from 'vue-sonner';
import MuvalTippy from '@components/MuvalTippy.vue';
import MPDatetime from '@components/inputs/MPDatetime.vue';
import MPModal from '@components/modal/MPModal.vue';
import MuvalSwitch from '@components/inputs/MuvalSwitch.vue';
import useEmitter from '@plugins/useEmitter';
import { actionTypeIcon, actionTypeLabel } from '@util/actionTypeMeta';
import { formatSalesActionDate, formatSalesActionTime } from '@util/salesActionSchedule';
import { salesQueueFooterHeight, toScheduleAtPayload, useSalesQueue } from '@composables/useSalesQueue';
import { useOrderStore } from '@store/order';
import { useUserStore } from '@store/user';

const appStore = useAppStore();
const orderStore = useOrderStore();
const userStore = useUserStore();
const emitter = useEmitter();
const router = useRouter();
const route = useRoute();

/**
 * Restricted field staff have no use for a sales follow-up queue, so this skips
 * the read on every page they open rather than spending a request per navigation.
 *
 * Note the gate is a product decision, NOT a permissions one: `orders-crud` — what
 * the read endpoint checks — *does* grant FIELD_STAFF and CONTRACTOR, so the call
 * would succeed, not 403. (Queue *writes* go through `sales-actions-manage`, which
 * excludes them.) An earlier version of this comment claimed a guaranteed 403;
 * that was wrong, and acting on it would have been a real leak, because a
 * client-side gate justified by a server-side denial that does not exist is no
 * gate at all.
 */
const canHoldQueue = computed(() => !userStore.isRestrictedFieldStaff?.());

const {
    query,
    queue,
    items,
    currentItem,
    currentAction,
    previousItem,
    nextItem,
    isPaused,
    isRunning,
    progress,
    isWriting,
    advance,
    moveCursor,
    resequence,
    setPaused,
    closeQueue,
} = useSalesQueue(canHoldQueue);

const footerEl = useTemplateRef('footerEl');

/**
 * Published for `AuthenticatedLayout` to reserve and for `App.vue`'s toaster to
 * clear. Measured rather than declared: the bar wraps on a narrow viewport, and a
 * hard-coded reservation would be wrong exactly when the overlap matters most.
 */
useResizeObserver(footerEl, ([entry]) => {
    salesQueueFooterHeight.value = entry?.contentRect ? entry.target.offsetHeight : 0;
});

watch(isRunning, (running) => {
    if (!running) {
        salesQueueFooterHeight.value = 0;
    }
});

onUnmounted(() => {
    salesQueueFooterHeight.value = 0;
});

/** The scheduler's bar takes the same inset, so the two agree about the sidebar. */
const sidebarInset = computed(() => (appStore.sideMini ? '4rem' : '16rem'));

const viewerTimezone = computed(() => userStore.getTimezone ?? dayjs.tz.guess());

/**
 * One date format for the bar and the panel alike. `formatSalesActionDate` /
 * `formatSalesActionTime` are what the list, the swimlane, the agenda and the
 * detail popover all render — a footer whose date read differently from the row it
 * was built from would be the more surprising outcome, and a panel that invented a
 * second format inside the same component would be worse again.
 */
const momentLabel = (action) => {
    if (!action) {
        return '';
    }

    const date = formatSalesActionDate(action, viewerTimezone.value);

    return action.all_day ? `${date} · All day` : `${date} · ${formatSalesActionTime(action, viewerTimezone.value)}`;
};

const scheduleLabel = computed(() => momentLabel(currentAction.value));

/** Split rather than joined, so the arrow between the two ends can be a glyph. */
const routeLeg = computed(() => {
    const order = currentAction.value?.order;

    if (!order?.pickup_locality && !order?.delivery_locality) {
        return null;
    }

    return { pickup: order.pickup_locality ?? '--', delivery: order.delivery_locality ?? '--' };
});

const segmentState = (item) =>
    item.done ? 'done'
    : item.skipped ? 'skipped'
    : 'pending';

/**
 * The cursor's own segment is drawn as the cursor whatever state the item is in —
 * where the agent *is* is the one thing the bar must not bury under a colour.
 */
const segmentClass = (item) => {
    if (item.id === queue.value?.current_item_id) {
        return 'bg-brand';
    }

    return { done: 'bg-muval-lime-1', skipped: 'bg-muval-amber', pending: 'bg-muval-gray-4' }[segmentState(item)];
};

/** The outcome in flight, so only the button that was pressed shows a spinner. */
const pendingOutcome = ref(null);

const showReschedule = ref(false);
const rescheduleEl = useTemplateRef('rescheduleEl');

/** The picker's own value and mode, seeded from the action each time it opens. */
const rescheduleAt = ref('');
const rescheduleAllDay = ref(false);

onClickOutside(rescheduleEl, () => {
    showReschedule.value = false;
});

/**
 * The action's own time of day, in the viewer's calendar.
 *
 * This is what the presets preserve: an action due at 10am moved to "Tomorrow" is
 * due at 10am tomorrow, not at whatever hour the button decided. An action that is
 * currently all-day has no time to preserve — the column's time component means
 * nothing for those — so one is chosen only if the agent turns all-day off.
 */
const ACTION_DEFAULT_HOUR = 9;

const actionTimeOfDay = computed(() => {
    const action = currentAction.value;

    if (!action?.schedule_at || action.all_day) {
        return { hour: ACTION_DEFAULT_HOUR, minute: 0 };
    }

    const at = dayjs(action.schedule_at).tz(viewerTimezone.value);

    return { hour: at.hour(), minute: at.minute() };
});

/** The value shape MPDatetime reads and writes, which depends on the mode. */
const composeMoment = (moment, allDay) => (allDay ? moment.format('YYYY-MM-DD') : moment.format('YYYY-MM-DD HH:mm'));

/**
 * The moments an agent actually picks. Each moves the *date* forward from today
 * and keeps the time of day, so a preset is a "when", not a "how much later" — an
 * overdue action moved to "Tomorrow" lands tomorrow rather than a day after the
 * date it already missed.
 */
const reschedulePresets = computed(() => {
    const allDay = rescheduleAllDay.value;
    const { hour, minute } = actionTimeOfDay.value;
    const today = dayjs().tz(viewerTimezone.value).hour(hour).minute(minute).second(0);

    return [
        { key: 'tomorrow', label: 'Tomorrow', days: 1 },
        { key: 'three-days', label: 'In 3 days', days: 3 },
        { key: 'next-week', label: 'Next week', days: 7 },
    ].map((preset) => {
        const moment = today.add(preset.days, 'day');

        return {
            ...preset,
            value: composeMoment(moment, allDay),
            hint: moment.format(allDay ? 'ddd D MMM' : 'ddd D MMM, h:mma'),
        };
    });
});

/**
 * Opening seeds the picker from the action, so the panel opens on where the
 * follow-up currently sits rather than on nothing — the common edit is a nudge,
 * not a fresh choice.
 */
function toggleReschedule() {
    showReschedule.value = !showReschedule.value;

    if (!showReschedule.value) {
        return;
    }

    const action = currentAction.value;

    rescheduleAllDay.value = Boolean(action?.all_day);
    rescheduleAt.value =
        action?.schedule_at ?
            composeMoment(
                action.all_day ?
                    dayjs(String(action.schedule_at).slice(0, 10))
                :   dayjs(action.schedule_at).tz(viewerTimezone.value),
                Boolean(action.all_day),
            )
        :   '';
}

/**
 * Toggling the mode reshapes the value the picker holds, rather than handing a
 * timed value to a date-only control (or the reverse) and letting it guess. The
 * action's own time of day comes back when a date is given one.
 */
watch(rescheduleAllDay, (allDay) => {
    if (!rescheduleAt.value) {
        return;
    }

    const { hour, minute } = actionTimeOfDay.value;
    const current = dayjs(rescheduleAt.value);

    rescheduleAt.value = composeMoment(allDay ? current : current.hour(hour).minute(minute), allDay);
});

async function exit(outcome, scheduleAt = undefined, allDay = undefined) {
    if (!queue.value || isWriting.value) {
        return;
    }

    pendingOutcome.value = outcome;

    try {
        await advance.mutateAsync({ queueId: queue.value.id, outcome, scheduleAt, allDay });
    } catch {
        // `useSalesQueue` surfaces the failure and the server moved nothing —
        // the cursor and the action are both where they were.
    } finally {
        pendingOutcome.value = null;
    }
}

/**
 * The wire outcome is still `SNOOZED` — the server's vocabulary for "moved, not
 * dealt with", and the thing the summary deliberately counts as outstanding. Only
 * the affordance is named for what an agent is actually doing.
 *
 * `all_day` travels with the moment because the two decide together how
 * `schedule_at` is read, and the server leaves the flag alone when it is not sent.
 */
async function reschedule(value, allDay) {
    if (!value || !currentAction.value) {
        return;
    }

    showReschedule.value = false;

    await exit('SNOOZED', toScheduleAtPayload(value, allDay, viewerTimezone.value), allDay);
}

/** Cursor-only moves. No outcome is written, so re-reading an item is not working it. */
async function stepTo(item) {
    if (!item || !queue.value || isWriting.value) {
        return;
    }

    await moveCursor.mutateAsync({ queueId: queue.value.id, itemId: item.id }).catch(() => {});
}

const jumpTo = (item) => stepTo(item);

/**
 * The expanded panel (design D21). Client state in a `ref`: nothing persists it,
 * no endpoint knows about it, and collapsing gives back the height D13 reserved.
 */
const isExpanded = ref(false);

/**
 * A stable mirror of the item list, not a fresh array per render.
 *
 * `vuedraggable` mutates the array it is given, in place, during a drag — so
 * binding a derived `[...items]` gives it something that is replaced on the next
 * render and the drop lands on a discarded array (`PriceGroup.vue:101` records the
 * same trap). The mirror is re-synced from the server's order on every change
 * *except* while a drag is in flight, when yanking the list out from under the
 * pointer is exactly what must not happen.
 */
const panelItems = ref([]);
const isDragging = ref(false);

/** The order to fall back to if the write fails — the panel is the only place it shows. */
let orderBeforeDrag = [];

watch(
    items,
    (next) => {
        if (isDragging.value) {
            return;
        }

        panelItems.value = [...next];
    },
    { immediate: true },
);

const dotClass = (item) => {
    if (!item.available) {
        return 'bg-muval-gray-6 ring-1 ring-muval-gray-4';
    }

    return { done: 'bg-muval-lime-1', skipped: 'bg-muval-amber', pending: 'bg-muval-gray-4' }[segmentState(item)];
};

function onDragStart() {
    isDragging.value = true;
    orderBeforeDrag = [...panelItems.value];
}

/**
 * The whole sequence goes at once (design D22). A partial list would let a stale
 * client re-order around items it did not know existed, so the server refuses
 * anything that does not name every item exactly once — and membership is frozen,
 * so this list always can. No order action is written and the cursor is untouched:
 * it is a pointer, so it stays on the same item through the renumbering.
 */
async function onDragEnd() {
    isDragging.value = false;

    const nextIds = panelItems.value.map((item) => item.id);
    const previousIds = orderBeforeDrag.map((item) => item.id);

    if (!queue.value || nextIds.join() === previousIds.join()) {
        return;
    }

    try {
        await resequence.mutateAsync({ queueId: queue.value.id, itemIds: nextIds });
    } catch {
        // The composable raises the toast. The order is put back because the panel
        // is the only surface it is visible on — leaving the dropped row where the
        // pointer left it would show an order the server never accepted.
        panelItems.value = orderBeforeDrag;
    }
}

/**
 * A row is the segment bar's jump, not a second navigation rule (design D9, D21).
 * The handle is excluded: a click that began a drag must not also move the cursor.
 */
function chooseRow(item, event) {
    if (event?.target?.closest?.('.queue-drag-handle')) {
        return;
    }

    return stepTo(item);
}

/** Which of the two stops is being confirmed, or null when nothing is. */
const stopIntent = ref(null);

const showStopConfirm = computed({
    get: () => stopIntent.value !== null,
    set: (open) => {
        if (!open) {
            stopIntent.value = null;
        }
    },
});

/**
 * What the confirmation says, split so the consequence can be coloured on its
 * own — ending a queue is the irreversible half and reads in red, pausing does
 * not pretend to be.
 */
const stopCopy = computed(() => {
    const outstanding = progress.value.pending;
    const one = outstanding === 1;
    const noun = one ? 'action' : 'actions';

    if (stopIntent.value === 'close') {
        return {
            title: 'End this queue?',
            body: 'The queue is finished for good, it cannot be reopened, and the list would have to be built again.',
            consequence:
                outstanding === 0 ?
                    'Nothing is outstanding, so nothing is left behind.'
                :   `${outstanding} outstanding ${noun} ${one ? 'stays exactly where it is' : 'stay exactly where they are'} - nothing is completed, cancelled or rescheduled.`,
            confirm: 'End queue',
        };
    }

    return {
        title: 'Pause this queue?',
        body: 'The bar disappears and the queue keeps its place. Pick it back up from Activity in the top bar.',
        consequence:
            outstanding === 0 ?
                'Nothing is outstanding, so there is nothing waiting on you.'
            :   `${outstanding} outstanding ${noun} will be waiting where you left ${one ? 'it' : 'them'}.`,
        confirm: 'Pause queue',
    };
});

function askToStop(intent) {
    if (!queue.value || isWriting.value) {
        return;
    }

    stopIntent.value = intent;
}

function confirmStop() {
    return stopIntent.value === 'close' ? close() : pause();
}

/**
 * Set aside rather than ended. The toast names where it went, because a bar that
 * vanishes with no trace is indistinguishable from one that was closed — and the
 * two do very different things to a half-worked day.
 */
async function pause() {
    if (!queue.value || isWriting.value) {
        return;
    }

    try {
        await setPaused.mutateAsync({ queueId: queue.value.id, paused: true });
        stopIntent.value = null;
        toast.success('Queue paused. Pick it back up under Activity.');
    } catch {
        // Reported by the composable; the queue is untouched, and the modal stays
        // open on the choice the agent made rather than dropping them back to a
        // footer that is still there.
    }
}

async function close() {
    if (!queue.value || isWriting.value) {
        return;
    }

    try {
        await closeQueue.mutateAsync({ queueId: queue.value.id });
        stopIntent.value = null;
        toast.success('Queue closed.');
    } catch {
        // Reported by the composable; the queue is untouched.
    }
}

/**
 * The footer drives the router: every cursor change takes the main pane to that
 * item's order, including the first item when a queue is created. `OrderView`
 * accepts no tab parameter — its path is `/orders/:id/view` and it reads no `tab`
 * query — so this always lands on the overview, resolving the design's open
 * question.
 *
 * The *first* resolution of the query is deliberately not a move. A reload must
 * restore the footer where the agent left it without hijacking the page they
 * reloaded; only a change made after that is a move the agent asked for.
 */
const hasHydrated = ref(false);
let lastItemId = null;

/**
 * The one rule about when the page is already where it needs to be. Shared with
 * the email affordance rather than restated there, so there is a single answer to
 * "is this navigation redundant".
 */
const isShowingOrder = (orderId) => route.name === 'OrderView' && Number(route.params.id) === Number(orderId);

function driveTo(item) {
    const orderId = item?.action?.order?.id;

    if (!orderId || isShowingOrder(orderId)) {
        return;
    }

    router.push({ name: 'OrderView', params: { id: orderId } });
}

/**
 * The email affordance (design D18). A `mailto:` handed the message to an external
 * client, so nothing about the email landed on the order it was about and nothing
 * recorded that the follow-up happened. This opens the CRM's own composer instead
 * — and that composer lives on the order screen, behind the three gates
 * (`canSendComms`, `isSafeToEmail`, `allowMoveFollowUpByEmail`) that decide whether
 * an order may be emailed at all. Routing through it means those keep gating and
 * the loaded order is the right one, for free.
 *
 * All of the waiting lives here rather than in the listener. `Order.vue` mounts
 * `ActionsPanel` behind `v-if="orderStore.getOrderId"`, so an emit fired straight
 * after `router.push` lands on nothing — the listener does not exist yet. So the
 * footer navigates exactly as an advance does, waits for the order store to arrive
 * at that order, and only then emits. `CommAction` stays a plain listener that
 * knows nothing about the queue.
 */
const COMPOSER_WAIT_MS = 15000;

const isComposingEmail = ref(false);
let stopWaitingForOrder = null;

/**
 * Fails closed, the same way the footer itself does: the control returns to its
 * resting state rather than spinning forever. The route has completed its move and
 * the queue's cursor was never touched, so nothing is left half-done.
 */
function stopComposing() {
    stopWaitingForOrder?.();
    stopWaitingForOrder = null;
    isComposingEmail.value = false;
}

function composeEmail() {
    const orderId = currentAction.value?.order?.id;

    if (!orderId || isComposingEmail.value) {
        return;
    }

    const openComposer = async () => {
        stopComposing();

        // `Order.vue` mounts `ActionsPanel` behind a `v-if` on the same store field
        // this waits on, so the listener comes into existence in the flush the
        // watcher below is running inside. One tick puts the emit after that render.
        await nextTick();

        emitter.emit('show:email-modal', { id: orderId, form: 'EMAIL' });
    };

    // Already on that order and already loaded: no navigation to make and nothing
    // to wait for, so the composer opens on the click.
    if (isShowingOrder(orderId) && Number(orderStore.getOrderId) === Number(orderId)) {
        openComposer();

        return;
    }

    isComposingEmail.value = true;

    if (!isShowingOrder(orderId)) {
        router.push({ name: 'OrderView', params: { id: orderId } });
    }

    const timer = setTimeout(() => {
        stopComposing();
        toast.error('That order did not load, so the email composer could not be opened.');
    }, COMPOSER_WAIT_MS);

    const unwatch = watch(
        () => orderStore.getOrderId,
        (loadedId) => {
            if (Number(loadedId) === Number(orderId)) {
                openComposer();
            }
        },
    );

    stopWaitingForOrder = () => {
        clearTimeout(timer);
        unwatch();
    };
}

/** A wait belongs to the item that started it, so a cursor move abandons it. */
watch(currentItem, () => stopComposing());

onUnmounted(() => stopComposing());

/**
 * Resuming is a deliberate act, so it takes the page with it — unlike a reload,
 * which restores the footer where the agent left it and must not hijack whatever
 * they reloaded. The cursor has not moved, so the watcher below would not fire.
 */
watch(isPaused, (paused, wasPaused) => {
    if (wasPaused && !paused) {
        driveTo(currentItem.value);
    }
});

watch(
    [() => query.isFetched.value, () => queue.value?.current_item_id ?? null],
    ([fetched, itemId]) => {
        if (!fetched) {
            return;
        }

        if (!hasHydrated.value) {
            hasHydrated.value = true;
            lastItemId = itemId;

            return;
        }

        if (itemId === lastItemId) {
            return;
        }

        lastItemId = itemId;
        driveTo(currentItem.value);
    },
    { immediate: true },
);
</script>
